<template>
    <div class="checkbox flex flex-row relative w-100"
        :class="{ 'checked': isChecked, 'cursor-not-allowed': disabled, 'cursor-pointer': !disabled }" @click="toggle">
        <!-- Label column only when this component owns the label: inside a
             PropertyRow the label is the row's own column, and an empty 40%
             spacer would push the box out of line with the other controls. -->
        <div v-if="hasDefaultSlot || label" class="w-2/5 shrink-0 min-w-0">
        <template v-if="hasDefaultSlot">
            <slot></slot>
        </template>
        <template v-else>
            <form-label v-if="label" class="checkbox__label my-auto" :color=color
                :class="{ 'cursor-not-allowed': disabled, 'cursor-pointer': !disabled }" v-html="label">
            </form-label>
        </template>
        </div>
        <div class="flex-1 min-w-0">
        <span class="checkbox__checkbox border border-gray-400 my-auto">
            <svg class="icon-svg checkbox__icon" viewBox="0 0 26 26">
                <path
                    d="m.3,14c-0.2-0.2-0.3-0.5-0.3-0.7s0.1-0.5 0.3-0.7l1.4-1.4c0.4-0.4 1-0.4 1.4,0l.1,.1 5.5,5.9c0.2,0.2 0.5,0.2 0.7,0l13.4-13.9h0.1v-8.88178e-16c0.4-0.4 1-0.4 1.4,0l1.4,1.4c0.4,0.4 0.4,1 0,1.4l0,0-16,16.6c-0.2,0.2-0.4,0.3-0.7,0.3-0.3,0-0.5-0.1-0.7-0.3l-7.8-8.4-.2-.3z" />
            </svg>
        </span>
        </div>
        <!-- Shared property status/reset column — inert unless :resettable is
             passed. @click.stop so hitting reset never also toggles the box. -->
        <property-override-indicator
            :resettable="resettable" :modified="modified" :inherited="inherited"
            @click.stop
            @reset="$emit('reset')" />
    </div>
</template>

<script>
import PropertyOverrideIndicator from '../properties/PropertyOverrideIndicator.vue';

export default {
    components: { PropertyOverrideIndicator },
    props: {
        modelValue: {
            type: [Boolean, Number],
            required: false
        },
        // Property override status — see PropertyOverrideIndicator.vue.
        resettable: { type: Boolean, default: null },
        modified: { type: Boolean, default: false },
        inherited: { type: Boolean, default: false },
        checked: {
            type: Boolean,
            required: false
        },
        label: {
            type: String,
            required: false
        },
        color: {
            required: false,
            type: String,
            default: 'primary'
        },
        disabled: {
            required: false,
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'change', 'click', 'input', 'reset'],
    data() {
        return {
            isChecked: this.checked === true
        }
    },
    mounted() {
        this.isChecked = this.modelValue ? this.modelValue : false;
    },
    methods: {
        toggle(e) {
            this.$emit('click', e);
            if (this.disabled) {
                return;
            }
            this.isChecked = !this.isChecked;
            this.$emit('update:modelValue', this.isChecked);
        }
    },
    watch: {
        modelValue: function (newVal) {
            this.isChecked = newVal;
        },
        checked: function (newVal) {
            this.isChecked = newVal;
        },
        isChecked: function () {
            this.$emit('change', this.isChecked);
            this.$emit('input', this.isChecked);
        }
    },
    computed: {
        hasDefaultSlot() {
            return !!this.$slots.default
        }
    }
}
</script>

<style scoped>
.checkbox,
.checkbox label {
    transition: 200ms;
}

.checkbox__checkbox {
    display: flex;
    border-radius: 3px;
    background-color: white;
    transition: border .1s ease-out, background-color .1s ease-out;
    height: 1.3rem;
    width: 1.3rem;
    min-height: 1.3rem;
    min-width: 1.3rem;
    left: 0;
}

.checkbox:hover .checkbox__checkbox {
    border-color: #a0aec0;
}

.checkbox.checked .checkbox__checkbox {
    background-color: #147cb8;
    border-color: #147cb8;
}

.checkbox__icon {
    width: 0.7rem;
    height: 0.7rem;
    margin: auto;
    transition: transform .2s ease-out;
    fill: white;
    transform: scale(0);
}

.checkbox.checked .checkbox__icon {
    transform: scale(1);
}
</style>